import {
  DIAGNOSTIC_OBSERVATION_ID_SYSTEM,
  DIAGNOSTIC_PROCEDURE_KIND,
  hasDiagnosticResourceKind,
  TREATMENT_PROCEDURE_KIND,
  TREATMENT_PROCEDURE_KIND_SYSTEM,
} from "@repo/api";
import { asMemberId, InvalidOrderSetError, type MemberId, OrderSet, type ParameterAnswer } from "@repo/domain";
import { readDiagnosticProcedure } from "../fhir/diagnostic-procedure-storage";
import { readOrderSet } from "../fhir/order-set-storage";
import { readProcedurePeriod } from "../fhir/procedure-period";
import { readTreatmentProcedure } from "../fhir/treatment-procedure-storage";
import type { ObservationRow, ProcedureRow, RequestGroupRow, ServiceRequestRow } from "../fhir/types";
import { isProcedureNotPerformed, procedureStatusOf } from "../lib/procedure-lifecycle";
import type { OrderSetReadDto } from "./order-set";

type ProcedureProjectionRow = ProcedureRow & { observations: ObservationRow[] };

export interface OrderSetProjectionRow {
  anchor: RequestGroupRow;
  orders: Array<
    ServiceRequestRow & {
      procedures: ProcedureProjectionRow[];
      observations: ObservationRow[];
    }
  >;
}

/**
 * RequestGroup and ServiceRequest subscription events can arrive separately.
 * The mapper-owned cardinality covers the whole aggregate, including exams
 * that deliberately sit outside RequestGroup.action. Timed references provide
 * the second half of the proof: the loaded rows must also be the members named
 * by the anchor, not merely the right number of children.
 */
export function isOrderSetGraphComplete(row: OrderSetProjectionRow): boolean {
  if (!row.anchor) return false;
  const expected = row.anchor.memberCount;
  if (!Array.isArray(row.orders) || !Number.isInteger(expected) || expected === undefined || expected < 0) return false;
  if (row.orders.length !== expected) return false;
  const loaded = new Set(row.orders.flatMap((order) => (order.id ? [order.id] : [])));
  const referenced = (row.anchor.action ?? []).flatMap((step) =>
    (step.action ?? []).flatMap((action) => {
      const reference = action.resource?.reference;
      return reference?.startsWith("ServiceRequest/") ? [reference.slice("ServiceRequest/".length)] : [];
    }),
  );
  return referenced.every((id) => loaded.has(id));
}

export function projectLoadedOrderSet(row: OrderSetProjectionRow): OrderSetReadDto | undefined {
  if (!isOrderSetGraphComplete(row)) return undefined;
  try {
    return projectOrderSet(row);
  } catch (error) {
    if (!(error instanceof InvalidOrderSetError)) throw error;
    // Independent subscriptions can deliver different aggregate versions. Keep
    // validation authoritative and report every rejection so persisted corruption
    // remains observable even if no later sync repairs the graph.
    console.error("Order set projection omitted an invalid graph", { requestGroupId: row.anchor.id }, error);
    return undefined;
  }
}

type OrderSetOrder = OrderSetReadDto["orders"][number];

function diagnosticProcedure(
  row: ProcedureProjectionRow | undefined,
): OrderSetOrder["diagnosticProcedure"] | undefined {
  // A struck record asserts nothing, so the card reads as it did before the
  // did-not-perform: no procedure, badge back to Not started.
  if (
    !row?.domainId ||
    row.status === "entered-in-error" ||
    !hasDiagnosticResourceKind(row, DIAGNOSTIC_PROCEDURE_KIND)
  ) {
    return undefined;
  }
  const facts = readDiagnosticProcedure(row, row.observations);
  const resourceByIdentity = new Map(
    row.observations.flatMap((observation) => {
      const identity = observation.identifier?.find(
        (identifier) => identifier.system === DIAGNOSTIC_OBSERVATION_ID_SYSTEM,
      )?.value;
      return identity && observation.id ? [[identity, observation.id] as const] : [];
    }),
  );
  const outcome =
    facts.lifecycle === "DidNotPerform"
      ? ("didNotPerform" as const)
      : facts.lifecycle === "UnableToComplete"
        ? ("unableToComplete" as const)
        : facts.lifecycle === "Performed"
          ? ("performed" as const)
          : undefined;
  const period = readProcedurePeriod(row.performedPeriod);
  return {
    id: facts.id,
    ...(period
      ? {
          startedAt: period.start,
          startSource: period.startSource,
          ...(period.end ? { endedAt: period.end, endSource: period.endSource } : {}),
        }
      : {}),
    ...(row.meta?.author?.display ? { lastWrittenBy: row.meta.author.display } : {}),
    uncaptured: [...facts.uncaptured],
    trials: facts.trials,
    // `InProgress` is the only lifecycle with no outcome, so the fallback is
    // exhaustive rather than a default.
    status: outcome ?? "inProgress",
    outcome,
    observations: facts.observations.flatMap((observation) => {
      const resourceId = resourceByIdentity.get(observation.observationId);
      if (!resourceId || observation.lifecycle === "EnteredInError") return [];
      return [
        {
          resourceId,
          observationId: observation.observationId,
          definitionId: observation.definitionId,
          ...(observation.trialId ? { trialId: observation.trialId } : {}),
          observedAt: observation.observedAt,
          values: observation.values.map((recorded) => ({
            ...recorded,
            value:
              recorded.value.kind === "Choice"
                ? { ...recorded.value, selected: [...recorded.value.selected] }
                : recorded.value,
          })),
          ...(observation.note ? { note: observation.note } : {}),
          ...(observation.derivedFrom
            ? {
                derivedFrom: {
                  documentId: observation.derivedFrom.documentId,
                  writtenBy: observation.derivedFrom.writtenBy === "Hand" ? ("hand" as const) : ("document" as const),
                },
              }
            : {}),
          status: observation.lifecycle === "Corrected" ? ("corrected" as const) : ("final" as const),
        },
      ];
    }),
    ...(facts.notes ? { notes: facts.notes } : {}),
  };
}

function observationDto(observation: ObservationRow): OrderSetOrder["observations"][number] | undefined {
  const coding = observation.code?.coding?.find((candidate) => candidate.code);
  const componentValue = observation.component?.map(displayObservationValue).filter(Boolean).join(", ");
  const value = displayObservationValue(observation) || componentValue;
  if (!coding?.code || !value) return undefined;
  return { code: coding.code, ...(coding.display ? { display: coding.display } : {}), value };
}

function displayObservationValue(value: ObservationRow | NonNullable<ObservationRow["component"]>[number]): string {
  if (value.valueQuantity?.value !== undefined) {
    const unit = value.valueQuantity.unit ?? value.valueQuantity.code;
    return `${value.valueQuantity.value}${unit ? ` ${unit}` : ""}`;
  }
  if (value.valueCodeableConcept) {
    return value.valueCodeableConcept.coding?.[0]?.display ?? value.valueCodeableConcept.text ?? "";
  }
  if (value.valueString !== undefined) return value.valueString;
  if (value.valueBoolean !== undefined) return value.valueBoolean ? "Yes" : "No";
  if (value.valueInteger !== undefined) return String(value.valueInteger);
  return "";
}

function flatCodeable(value: ObservationRow["code"] | ProcedureRow["code"] | undefined) {
  const coding = value?.coding?.[0];
  return {
    ...(coding?.system ? { system: coding.system } : {}),
    ...(coding?.code ? { code: coding.code } : {}),
    ...(coding?.display ? { display: coding.display } : {}),
    ...(value?.text ? { text: value.text } : {}),
  };
}

function flatQuantity(value: ObservationRow["valueQuantity"] | undefined) {
  if (!value) return undefined;
  return {
    ...(value.value !== undefined ? { value: value.value } : {}),
    ...(value.unit ? { unit: value.unit } : {}),
    ...(value.system ? { system: value.system } : {}),
    ...(value.code ? { code: value.code } : {}),
  };
}

function projectChartingObservation(observation: ObservationRow): OrderSetOrder["chartingObservations"][number] {
  return {
    observationId: observation.domainId ?? "",
    status: observation.status,
    code: flatCodeable(observation.code),
    ...(observation.effectiveDateTime ? { effectiveDateTime: observation.effectiveDateTime } : {}),
    ...(observation.valueString !== undefined ? { valueString: observation.valueString } : {}),
    ...(flatQuantity(observation.valueQuantity) ? { valueQuantity: flatQuantity(observation.valueQuantity) } : {}),
    ...(observation.valueCodeableConcept
      ? { valueCodeableConcept: flatCodeable(observation.valueCodeableConcept) }
      : {}),
    ...(observation.valueInteger !== undefined ? { valueInteger: observation.valueInteger } : {}),
    ...(observation.valueBoolean !== undefined ? { valueBoolean: observation.valueBoolean } : {}),
    ...(observation.component?.length
      ? {
          components: observation.component.map((component) => ({
            code: flatCodeable(component.code),
            ...(component.valueString !== undefined ? { valueString: component.valueString } : {}),
            ...(flatQuantity(component.valueQuantity) ? { valueQuantity: flatQuantity(component.valueQuantity) } : {}),
            ...(component.valueCodeableConcept
              ? { valueCodeableConcept: flatCodeable(component.valueCodeableConcept) }
              : {}),
            ...(component.valueInteger !== undefined ? { valueInteger: component.valueInteger } : {}),
            ...(component.valueBoolean !== undefined ? { valueBoolean: component.valueBoolean } : {}),
          })),
        }
      : {}),
    ...(observation.note?.length ? { notes: observation.note.map((note) => ({ text: note.text ?? "" })) } : {}),
  };
}

function projectChartedProcedure(procedure: ProcedureRow): NonNullable<OrderSetOrder["chartedProcedure"]> {
  const status = procedureStatusOf(procedure.status);
  if (!status) throw new Error(`Procedure '${procedure.id}' has no readable status`);
  return {
    procedureId: procedure.domainId ?? "",
    // No patientId/encounterId here: they were the Patient and Encounter ROW keys, nothing read
    // them, and an exported DTO does not carry a locator. The domain values live on the row
    // (`patientDomainId`, `visitId`) if a consumer ever needs them.
    status,
    ...(procedure.statusReason ? { statusReason: flatCodeable(procedure.statusReason) } : {}),
    code: flatCodeable(procedure.code),
    ...(procedure.note?.length ? { notes: procedure.note.map((note) => ({ text: note.text ?? "" })) } : {}),
  };
}

function treatmentProcedure(
  procedure: ProcedureProjectionRow | undefined,
): OrderSetOrder["treatmentProcedure"] | undefined {
  if (!procedure?.domainId) return undefined;
  const facts = readTreatmentProcedure(procedure, procedure.observations);
  // A struck record asserts nothing, so the card reads as it did before the
  // did-not-perform: no procedure, badge back to Not started.
  if (facts.status === "enteredInError") return undefined;
  return {
    procedureId: facts.id,
    status: facts.status,
    performerIds: [...facts.performerIds],
    ...(facts.performedPeriod ? { performedPeriod: { ...facts.performedPeriod } } : {}),
    ...(facts.note === undefined ? {} : { note: facts.note }),
    trials: facts.trials,
    observations: facts.observations.map((observation) => ({
      observationId: observation.observationId,
      ...(observation.trialId ? { trialId: observation.trialId } : {}),
      definitionId: String(observation.definitionId),
      observedAt: observation.observedAt,
      components: observation.components.map((component) => ({
        componentId: String(component.componentId),
        value:
          component.value.kind === "Choice"
            ? { ...component.value, selected: [...component.value.selected] }
            : { ...component.value },
      })),
      ...(observation.note === undefined ? {} : { note: observation.note }),
      lifecycle: { ...observation.lifecycle },
    })),
    uncaptured: facts.uncaptured.map(String),
  };
}

/** Recover the domain member identity from the projection's `<orderSetId>:<memberId>` key.
 * Scoped keys stay on the read side; booking inputs name the member within their order set.
 */
export function orderSetStepMemberId(stepId: string): MemberId {
  return asMemberId(stepId.slice(stepId.lastIndexOf(":") + 1));
}

export function projectOrderSet(row: OrderSetProjectionRow): OrderSetReadDto {
  const set = OrderSet.restore(readOrderSet(row.anchor, row.orders));
  const snapshot = set.snapshot();
  if (!snapshot.id) throw new Error("A stored OrderSet has no id");
  const orderRows = new Map(row.orders.flatMap((order) => (order.orderId ? [[order.orderId, order] as const] : [])));

  const steps = snapshot.steps.map((step) => ({
    stepId: `${snapshot.id}:${step.id}`,
    placement: { ...step.placement },
    orders: step.orders.flatMap((order): OrderSetOrder[] => {
      // The SNAPSHOT decides an order exists; `stored` is only the charted
      // overlay. Nothing below reads `stored.id`, and gating on it made an order
      // vanish whenever its ServiceRequest row was merely absent from the
      // collection — the RequestGroup-before-ServiceRequest sync window, or a
      // patient past `FHIR_SEARCH_PAGE_SIZE` ServiceRequests. A projection does
      // not drop rows, and it does not throw.
      const stored = orderRows.get(order.orderId);
      const procedures = stored?.procedures ?? [];
      const diagnosticRecord = diagnosticProcedure(
        procedures.find((procedure) => hasDiagnosticResourceKind(procedure, DIAGNOSTIC_PROCEDURE_KIND)),
      );
      const chartedProcedure = procedures.find(
        (procedure) =>
          Boolean(procedure.domainId) &&
          // Same as the diagnostic above: a struck record is no record.
          procedure.status !== "entered-in-error" &&
          procedure.meta?.tag?.some(
            (tag) => tag.system === TREATMENT_PROCEDURE_KIND_SYSTEM && tag.code === TREATMENT_PROCEDURE_KIND,
          ),
      );
      const treatmentRecord = treatmentProcedure(chartedProcedure);
      return [
        {
          orderId: order.orderId,
          definition: { url: order.definition.url, version: order.definition.version },
          ...(order.qualifier ? { qualifier: order.qualifier } : {}),
          parameterValues: order.parameterValues.map(projectParameterAnswer),
          ...(order.observationLevels?.length ? { observationLevels: order.observationLevels } : {}),
          ...(order.plannedTrials?.length
            ? {
                plannedTrials: order.plannedTrials.map((trial) => ({
                  id: String(trial.id),
                  values: trial.values.map(projectParameterAnswer),
                })),
              }
            : {}),
          status: order.status,
          ...(order.sourceOccurrenceId ? { sourceOccurrenceId: String(order.sourceOccurrenceId) } : {}),
          ...(order.revokedAt ? { revokedAt: order.revokedAt } : {}),
          ...(order.visitId ? { visitId: order.visitId } : {}),
          ...(procedures[0]?.note?.[0]?.text ? { procedureNote: procedures[0].note[0].text } : {}),
          charted: Boolean(treatmentRecord || diagnosticRecord),
          notPerformed: isProcedureNotPerformed(treatmentRecord?.status ?? diagnosticRecord?.status),
          observations: (stored?.observations ?? []).flatMap((observation) => {
            const projected = observationDto(observation);
            return projected ? [projected] : [];
          }),
          chartingObservations: (stored?.observations ?? []).map(projectChartingObservation),
          ...(chartedProcedure ? { chartedProcedure: projectChartedProcedure(chartedProcedure) } : {}),
          ...(treatmentRecord ? { treatmentProcedure: treatmentRecord } : {}),
          ...(diagnosticRecord ? { diagnosticProcedure: diagnosticRecord } : {}),
        },
      ];
    }),
  }));
  const allOrders = steps.flatMap((step) => step.orders);
  const activeOrders = allOrders.filter((order) => order.status !== "revoked");
  const current = allOrders.find(
    (order) => !order.charted && order.status !== "revoked" && order.status !== "completed",
  );
  const visitId = snapshot.steps.flatMap((step) => step.orders).find((order) => order.visitId)?.visitId;

  return {
    orderSetId: snapshot.id,
    stepIdHigh: snapshot.stepIdHigh,
    authoredEnvelopeMinutes: snapshot.envelopeMinutes,
    patientId: snapshot.patientId,
    ...(snapshot.protocol ? { protocol: { url: snapshot.protocol.url, version: snapshot.protocol.version } } : {}),
    envelopeMinutes: set.durationMinutes,
    durationMinutes: set.durationMinutes,
    orderedAt: snapshot.orderedAt,
    ...(snapshot.carePlanId ? { carePlanId: snapshot.carePlanId } : {}),
    ...(snapshot.curationRank !== undefined ? { curationRank: snapshot.curationRank } : {}),
    status: snapshot.status,
    ...(visitId ? { visitId } : {}),
    steps,
    orders: allOrders,
    procedureCount: activeOrders.length,
    ...(current
      ? {
          currentOrder: {
            orderId: current.orderId,
            definitionUrl: current.definition.url,
          },
        }
      : {}),
  };
}

function projectParameterAnswer(answer: ParameterAnswer) {
  return { source: String(answer.source), parameterId: String(answer.parameterId), value: { ...answer.value } };
}

export function projectOrderSetRows(rows: readonly OrderSetProjectionRow[]): OrderSetReadDto[] {
  return rows
    .filter((row) => row.anchor.isRedesignedOrderSet)
    .flatMap((row) => {
      const projected = projectLoadedOrderSet(row);
      return projected ? [projected] : [];
    });
}
